<script setup lang="ts">
import { EIcon, GLOBAL_SHORTCUTS, useShortcut } from '@erpx/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSidebar from '@/components/AppSidebar.vue'
import AppTopBar from '@/components/AppTopBar.vue'
import CommandPalette from '@/components/CommandPalette.vue'
import ShortcutSheet from '@/components/ShortcutSheet.vue'
import { tParts } from '@/i18n'

const router = useRouter()
const { locale } = useI18n()

const paletteOpen = ref(false)
const sheetOpen = ref(false)
const navCollapsed = ref(false)

/**
 * Descriptions are registered as catalogue *keys*, not prose. The registry
 * captures the string once at mount (`ui/src/lib/keys.ts`), so a literal would
 * freeze the cheat sheet in the language the shell mounted in; `ShortcutSheet`
 * resolves the key at render instead.
 */
useShortcut(
  GLOBAL_SHORTCUTS.commandPalette,
  () => {
    paletteOpen.value = true
  },
  {
    describe: { description: 'shell.shortcuts.commandPalette', group: 'global' },
    allowInInput: true,
  },
)

useShortcut(
  GLOBAL_SHORTCUTS.cheatSheet,
  () => {
    sheetOpen.value = !sheetOpen.value
  },
  { describe: { description: 'shell.shortcuts.cheatSheet', group: 'global' } },
)

useShortcut(
  GLOBAL_SHORTCUTS.quickCreate,
  () => {
    void router.push({ name: 'tenant-create' })
  },
  { describe: { description: 'shell.shortcuts.quickCreate', group: 'global' } },
)

/**
 * The footer hint wraps a `<kbd>` around one word of its own sentence. Splitting
 * the *translated* message at its `{key}` placeholder keeps the key in whatever
 * position the language puts it, which concatenating two fragments could not.
 */
const hintParts = computed(() => {
  void locale.value
  return tParts('shell.footerHint', 'key')
})
</script>

<template>
  <div class="flex min-h-screen flex-col bg-app text-primary">
    <a class="a-skip-link" href="#workspace">{{ $t('shell.skipToContent') }}</a>

    <AppTopBar
      :nav-collapsed="navCollapsed"
      @toggle-nav="navCollapsed = !navCollapsed"
      @open-palette="paletteOpen = true"
      @open-shortcuts="sheetOpen = true"
    />

    <div class="flex flex-1 items-stretch">
      <AppSidebar :collapsed="navCollapsed" />

      <div class="min-w-0 flex-1">
        <AppBreadcrumbs />
        <main id="workspace" class="px-6 pb-12 pt-2" tabindex="-1">
          <slot />
        </main>
      </div>
    </div>

    <footer
      class="flex items-center gap-2 border-t border-subtle px-6 py-2 text-2xs text-muted"
    >
      <EIcon name="command" :size="12" />
      <span>
        <!-- The key cap is the literal character the binding listens for, not prose. -->
        {{ hintParts[0] }}<kbd class="rounded-sm border border-subtle px-1">?</kbd>{{ hintParts[1] }}
      </span>
    </footer>

    <CommandPalette v-model:open="paletteOpen" />
    <ShortcutSheet v-model:open="sheetOpen" />
  </div>
</template>
